import { createContext, useContext, useMemo, ReactNode } from 'react';
import { BRAND_CONFIGS, DEFAULT_BRAND, BrandConfig } from '@/constants/branding';
import { BrandMode } from '@/constants/branding';
import { IS_MOBILE_APP, BRAND_MODE } from '@/constants/env';

interface BrandProviderProps {
  children: ReactNode;
}

const BrandContext = createContext<BrandConfig>(DEFAULT_BRAND);

function getBrandMode(): BrandMode | undefined {
  if (typeof window === 'undefined') return undefined;
  
  // For mobile apps, use the build-time environment variable
  if (IS_MOBILE_APP && BRAND_MODE) {
    if (Object.values(BrandMode).includes(BRAND_MODE as BrandMode)) {
      return BRAND_MODE as BrandMode;
    }
  }
  
  // For web apps, use URL parameters or domain detection
  const params = new URLSearchParams(window.location.search);
  const brandParam = params.get('brand');
  // Allow override by ?brand=mode
  if (brandParam && Object.values(BrandMode).includes(brandParam as BrandMode)) {
    return brandParam as BrandMode;
  }
  const host = window.location.hostname.toLowerCase();
  if (host.includes('munshiji')) return BrandMode.Munshiji;
  if (host.includes('kirana360')) return BrandMode.Kirana360;
  
  // Default to MunshiJi.AI for localhost development
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    return BrandMode.Munshiji;
  }
  
  return undefined;
}

export const BrandProvider = ({ children }: BrandProviderProps) => {
  const brandMode = getBrandMode();
  const brand = useMemo(() => {
    const found = Object.values(BRAND_CONFIGS).find(cfg => cfg.mode === brandMode);
    return found || DEFAULT_BRAND;
  }, [brandMode]);
  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
};

export function useBrand() {
  return useContext(BrandContext);
}

